import { Link } from "@tanstack/react-router";
import { parseISO, calendarDatePath, dayOfWeek, DAY_NAMES_SHORT_ID } from "@/lib/calendar/dates";
import { getHolidaysForMonth } from "@/data/holidays";
import { getHariPentingForMonth } from "@/data/hari-penting";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName, translateHariPenting } from "@/i18n/holidays";

interface Props {
  year: number;
  month: number;
}

/**
 * Compact, borderless legend list shown beneath each mini month card
 * in year-overview grids. Lists libur / cuti / hari penting with
 * small colored dots and links to the date detail page.
 */
export function MiniMonthLegend({ year, month }: Props) {
  const { t, href, locale } = useLocale();
  const holidays = getHolidaysForMonth(year, month);
  const libur = holidays.filter((h) => h.type === "libur");
  const cuti = holidays.filter((h) => h.type === "cuti-bersama");
  const hariPenting = getHariPentingForMonth(month);

  if (libur.length === 0 && cuti.length === 0 && hariPenting.length === 0) return null;

  const daysShort = locale === "en"
    ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    : DAY_NAMES_SHORT_ID;

  function row(iso: string, color: "red" | "blue" | "gray", label: string, key: string) {
    const p = parseISO(iso)!;
    const dow = dayOfWeek(p.y, p.m, p.d);
    const dotClass =
      color === "red" ? "bg-red-date" : color === "blue" ? "bg-cuti-bersama" : "bg-hari-penting";
    const txtClass =
      color === "red" ? "text-red-date font-medium"
        : color === "blue" ? "text-cuti-bersama font-medium"
        : "text-foreground";
    return (
      <li key={key} className="flex items-start gap-1.5 text-xs leading-snug">
        <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
        <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
          <span className="text-muted-foreground">{daysShort[dow]}, </span>
          <span className={txtClass}>{p.d} {t.common.months[p.m - 1]}</span>
          <span className="text-muted-foreground"> - </span>
          <span>{label}</span>
        </Link>
      </li>
    );
  }

  return (
    <ul className="mt-2 space-y-1 rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
      {libur.map((h) => row(h.date, "red", translateHolidayName(h.name, locale), `l-${h.date}-${h.name}`))}
      {cuti.map((h) => row(h.date, "blue", translateHolidayName(h.name, locale), `c-${h.date}-${h.name}`))}
      {hariPenting.map((h) => {
        const [mm, dd] = h.monthDay.split("-").map((n) => parseInt(n, 10));
        const iso = `${year}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
        return row(iso, "gray", translateHariPenting(h.name, locale), `p-${h.monthDay}-${h.name}`);
      })}
    </ul>
  );
}